import React from "react";
import {
  House,
  ChatCircleText,
  Briefcase,
  FileMagnifyingGlass,
  Users,
  Funnel,
  CalendarBlank,
  PaperPlaneTilt,
  ChartLineUp,
  Headset,
  CircleNotch,
  Gear,
  ShieldCheck,
  SignOut,
  Table,
  VideoCamera,
} from "@phosphor-icons/react";

const NAV = [
  { key: "welcome", label: "Welcome", icon: House },
  { key: "hr-chat", label: "Chat with HR Agent", icon: ChatCircleText },
  { key: "requisitions-manage", label: "Requisitions", icon: Briefcase },
  { key: "requisition", label: "Complete Job Requisition", icon: Briefcase },
  { key: "cv", label: "CV Evaluation (ATS)", icon: FileMagnifyingGlass },
  { key: "candidates", label: "Candidate List", icon: Users },
  { key: "dashboard", label: "Recruitment Dashboard", icon: Table },
  { key: "screening", label: "Candidate Screening", icon: Funnel },
  { key: "interviews", label: "Schedule Interviews", icon: CalendarBlank },
  { key: "ai-interviews", label: "AI Video Interviews", icon: VideoCamera },
  { key: "communication", label: "Communication", icon: PaperPlaneTilt },
  { key: "status", label: "Candidate Status", icon: CircleNotch },
  { key: "insights", label: "Insights", icon: ChartLineUp },
  { key: "applicant", label: "Applicant Support Chat", icon: Headset },
];

const ADMIN_NAV = [
  { key: "admin-settings", label: "Admin Settings", icon: ShieldCheck },
];

export default function Sidebar({ current, onChange, user, onLogout, statusFilter, setStatusFilter, locationFilter, setLocationFilter, requisitionFilter, setRequisitionFilter, requisitionOptions = [] }) {

  return (
    <aside
      data-testid="app-sidebar"
      className="w-72 border-r flex-shrink-0 bg-white flex flex-col h-full overflow-y-auto"
    >
      {/* Brand */}
      <div className="px-6 pt-6 pb-5 border-b">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-sm grid place-items-center text-white font-display font-bold"
            style={{ background: "#002FA7" }}
          >
            V
          </div>
          <div className="leading-tight">
            <div className="font-display text-[15px] font-bold tracking-tight">Vaku HCM</div>
            <div className="text-[11px] text-slate-500 font-medium">Recruit · Agentic AI</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="px-3 py-4">
        <div className="label-eyebrow px-3 mb-2">Navigation</div>
        <nav className="flex flex-col" data-testid="sidebar-nav">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = current === item.key;
            return (
              <div
                key={item.key}
                data-testid={`nav-${item.key}`}
                className={`nav-link ${active ? "active" : ""}`}
                onClick={() => onChange(item.key)}
                role="button"
                tabIndex={0}
              >
                <Icon size={16} weight={active ? "fill" : "regular"} />
                <span>{item.label}</span>
              </div>
            );
          })}
          {user?.role === "admin" && (
            <>
              <div className="label-eyebrow px-3 mt-3 mb-1">Admin</div>
              {ADMIN_NAV.map((item) => {
                const Icon = item.icon;
                const active = current === item.key;
                return (
                  <div
                    key={item.key}
                    data-testid={`nav-${item.key}`}
                    className={`nav-link ${active ? "active" : ""}`}
                    onClick={() => onChange(item.key)}
                    role="button"
                    tabIndex={0}
                  >
                    <Icon size={16} weight={active ? "fill" : "regular"} />
                    <span>{item.label}</span>
                  </div>
                );
              })}
            </>
          )}
        </nav>
      </div>

      {/* Filter Options */}
      <div className="px-5 pt-2 pb-6 border-t">
        <div className="label-eyebrow mb-2">Filter Options</div>
        <label className="text-[11px] text-slate-500 block mb-1">Status</label>
        <select
          data-testid="sidebar-filter-status"
          className="input mb-3"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {["All", "New", "Screening", "Interview", "Offer", "Hired", "Rejected"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <label className="text-[11px] text-slate-500 block mb-1">Location</label>
        <select
          data-testid="sidebar-filter-location"
          className="input mb-3"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
        >
          {["All", "Riyadh", "Jeddah", "Dammam"].map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
        <label className="text-[11px] text-slate-500 block mb-1">Requisition</label>
        <select
          data-testid="sidebar-filter-requisition"
          className="input"
          value={requisitionFilter}
          onChange={(e) => setRequisitionFilter(e.target.value)}
        >
          <option value="All">All</option>
          <option value="__unassigned__">— Unassigned —</option>
          {requisitionOptions.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-auto px-5 py-4 border-t">
        {user && (
          <div data-testid="user-card" className="mb-2">
            <div className="text-[11px] font-mono text-slate-500 truncate">{user.email}</div>
            <div className="text-[11px] text-slate-400">{user.tenantName} · <span className={user.role === "admin" ? "text-[#002FA7] font-semibold" : ""}>{user.role}</span></div>
            <button data-testid="logout-btn" className="btn btn-outline w-full !py-1 !text-[11px] mt-2" onClick={onLogout}>
              <SignOut size={12} /> Sign out
            </button>
          </div>
        )}
        <div className="text-[10px] text-slate-400 font-mono">v1.0 · Vaku HCM</div>
      </div>
    </aside>
  );
}
